// Global
import { ComponentRendering } from '@sitecore-jss/sitecore-jss-nextjs';
import React from 'react';
import { tv } from 'tailwind-variants';

// Local
import { withStandardComponentWrapper } from 'helpers/HOC';
import { ComponentProps } from 'lib/component-props';
import { findComponent } from 'lib/utils/object-utils';
import PlainTextWrapper from 'helpers/SitecoreWrappers/PlainTextWrapper/PlainTextWrapper';
import { smoothScrollToElement } from 'lib/hooks/useScrollElementIntoView';
import LinkWrapper from 'helpers/SitecoreWrappers/LinkWrapper/LinkWrapper';
import { EditingHelpText } from 'helpers/Editing/EditingHelpText';
import { SideNavContextProvider, useSideNavContext } from 'helpers/Context/SideNavContext';
import { Layout } from '.generated/Feature.CW.model';
import { focusUnfocusableElement } from 'lib/utils/dom-utils/tab';
import { CheckboxParamType } from 'lib/component-props/rendering-param-types';
import { AccordionContextProvider } from 'helpers/Context/AccordionContext';
import { PlaceholderWrapper } from 'helpers/SitecoreWrappers/PlaceholderWrapper/PlaceholderWrapper';
import classNames from 'classnames';
import useIsEditing from 'lib/hooks/useIsEditing';

const TAILWIND_VARIANTS = tv({
  slots: {
    base: ['sidenav-container'],
    mainGrid: ['grid', 'gap-4'],
    sideNav: ['top-24'],
    sideNavItem: ['absolute', 'inset-x-0', 'bottom-0', 'h-0.5', 'bg-black'],
    navTitleLinkWrapper: [
      'font-semibold',
      'relative',
      'group',
      'text-theme-black-800',
      'lg:text-xl',
    ],
    mainContent: ['xl:ps-0'],
  },
  variants: {
    isActive: {
      true: {
        sideNavItem: [],
      },
      false: {
        sideNavItem: [
          'transform',
          'origin-left',
          'scale-x-0',
          'transition-transform',
          'duration-300',
          'ease-out',
          'group-hover:scale-x-100',
        ],
      },
    },
  },
});

export type SideNavContainerProps = ComponentProps & {
  params: {
    singleOpenPanel?: CheckboxParamType;
    scrollToOpenPanel?: CheckboxParamType;
    openByDefault?: CheckboxParamType;
  };
};

function SideNavItem({ item }: { item: ComponentRendering & Layout.SideNavSection }) {
  const { navTitleLinkWrapper } = TAILWIND_VARIANTS();

  const { activeEntry } = useSideNavContext();
  const isEditing = useIsEditing();

  const isActive = !isEditing ? activeEntry?.uid === item.uid : false;
  const phKey = `hztl-section-content-${item?.params?.DynamicPlaceholderId}`;

  if (
    item?.placeholders?.[phKey]?.length === 0 &&
    item?.fields?.subHeading?.value === '' &&
    !isEditing
  ) {
    return <></>;
  }

  const TextWrapItem = () => (
    <div
      className={classNames(
        'block w-full font-semibold text-14 font-centuryGothic py-7 leading-5 px-8 rounded-sm overflow-hidden relative transition-all',
        'before:absolute before:h-full before:bg-blue-base before:left-0 before:top-0 before:pointer-events-none',
        'hover:bg-blue-50 hover:text-blue-700',
        isActive ? 'bg-blue-50 text-blue-700 before:w-2' : 'text-gray-500'
      )}
    >
      <PlainTextWrapper
        tag="div"
        field={item.fields?.sideNavHeading}
        fallbacks={[item.fields?.heading]}
      />
    </div>
  );

  return isEditing ? (
    <TextWrapItem />
  ) : (
    <LinkWrapper
      className={classNames(navTitleLinkWrapper(), 'block w-full')}
      ctaVariant="link"
      field={{ href: '#', anchor: 'accordion-' + item.uid }}
      role="menuitem"
      data-accordionItemId={`accordion-${item.uid}`}
      onClick={(e) => {
        e.preventDefault();
        const elem = document.getElementById('accordion-' + item.uid);
        if (elem) {
          smoothScrollToElement(elem, 'header');
          focusUnfocusableElement(elem, true);
        }
      }}
      isHashLink={true}
    >
      <TextWrapItem />
    </LinkWrapper>
  );
}

const SideNavContainer = (props: SideNavContainerProps): JSX.Element => {
  const { DynamicPlaceholderId } = props?.params || {};
  const isEditing = useIsEditing();
  const phKey = `side-nav-container-${DynamicPlaceholderId}`;
  const phParentKey = `parent-child-side-nav-container-${DynamicPlaceholderId}`;
  const variantName =
    props?.params?.FieldNames?.toLowerCase() == 'sidenav'
      ? 'default'
      : props?.params?.FieldNames?.toLowerCase();

  // Fix: Check if the first item has a 'uid' property before accessing it
  const sidenavuid: string =
    props?.rendering?.placeholders?.[phKey] &&
    Array.isArray(props.rendering.placeholders[phKey]) &&
    typeof props.rendering.placeholders[phKey][0] === 'object' &&
    'uid' in props.rendering.placeholders[phKey][0]
      ? (props.rendering.placeholders[phKey][0] as { uid: string }).uid
      : '';

  /*
   * State
   */

  /*
   * Rendering
   */

  const { base, mainGrid, sideNav, mainContent } = TAILWIND_VARIANTS();

  const accordionItems = findComponent(props.rendering, 'SideNavSection');
  const singlePanelOpen = props.params.singleOpenPanel === '1';

  const scrollToOpenPanel = props.params.scrollToOpenPanel === '1';

  const defaultOpenPanels: SideNavContainerProps['rendering'][] = [];

  if (props.params.openByDefault === '1') {
    if (singlePanelOpen) {
      defaultOpenPanels.push(accordionItems[0]);
    } else {
      defaultOpenPanels.push(...accordionItems);
    }
  }

  const parentChildNavPattern = /^parent-child-side-nav-container/;
  const isParentChildSideNav = Object.keys(props?.rendering?.placeholders || {}).some((key) => {
    const placeholders = props?.rendering?.placeholders;
    return parentChildNavPattern.test(key) && (placeholders?.[key]?.length ?? 0) > 0;
  });

  return (
    <section
      className={base()}
      data-component="authorable/shared/layout/side-nav-mobile-accordion"
      id={props.rendering.uid}
    >
      <SideNavContextProvider entries={accordionItems}>
        <div
          className={classNames(
            mainGrid(),
            (variantName == 'default' ||
              variantName == 'withoutaccordiononmobile' ||
              variantName == 'parentchildlayout') &&
              'max-w-screen-xxl mx-auto md:px-10 lg:px-20 pb-24',
            variantName == 'staticfulllayout' || variantName == 'staticsidebarlayout'
              ? 'grid-cols-1'
              : 'grid-cols-4'
          )}
        >
          {variantName == 'default' ||
          variantName == 'withoutaccordiononmobile' ||
          (variantName == 'parentchildlayout' && (isParentChildSideNav || isEditing)) ? (
            <div
              className={
                variantName == 'parentchildlayout'
                  ? 'block col-span-4 md:col-span-1'
                  : 'hidden md:block col-span-1'
              }
            >
              <aside
                className={classNames(
                  sideNav(),
                  variantName == 'parentchildlayout' ? 'static' : 'sticky'
                )}
              >
                {variantName == 'parentchildlayout' ? (
                  <div className="max-w-full md:max-w-[250px] mt-10 md:mt-24 px-10 md:px-0">
                    <PlaceholderWrapper name={phParentKey} rendering={props.rendering} />
                  </div>
                ) : (
                  <>
                    <EditingHelpText>
                      Edit below to give a different text for the sidebar. If left blank, it will
                      show the section header in the real site.
                    </EditingHelpText>
                    <nav
                      aria-label="Jump Links Navigation"
                      className="shadow-default p-8 rounded-md max-w-[250px] mt-24 bg-white"
                    >
                      <ul className="max-h-[calc(100vh-100px)] overflow-auto" role="menu">
                        {accordionItems.map((item) => (
                          <li key={item.uid} className="px-2 py-1" role="none">
                            <SideNavItem item={item} />
                          </li>
                        ))}
                      </ul>
                    </nav>
                  </>
                )}
              </aside>
            </div>
          ) : (
            <></>
          )}

          <div
            className={classNames(
              mainContent(),
              variantName == 'parentchildlayout' && !isParentChildSideNav && !isEditing
                ? 'col-span-4'
                : 'col-span-4 md:col-span-3'
            )}
          >
            <div
              className={classNames(
                'w-full',
                variantName == 'staticfulllayout' || variantName == 'staticsidebarlayout'
                  ? 'm-auto'
                  : ''
              )}
            >
              <AccordionContextProvider
                panels={accordionItems}
                singleOpenPanel={singlePanelOpen}
                scrollToOpenPanel={scrollToOpenPanel}
                defaultOpenPanels={defaultOpenPanels}
                variantName={variantName}
              >
                <PlaceholderWrapper
                  name={phKey}
                  rendering={props.rendering}
                  renderMobileAsAccordion={variantName === 'default'}
                  variantName={variantName}
                  isParentChildSideNav={
                    variantName == 'parentchildlayout' ? isParentChildSideNav : false
                  }
                  firstSectionUid={sidenavuid}
                />
              </AccordionContextProvider>
            </div>
          </div>
        </div>
      </SideNavContextProvider>
    </section>
  );
};

export const Default = withStandardComponentWrapper(SideNavContainer, false);
export const SideNav = withStandardComponentWrapper(SideNavContainer, false);
export const WithoutAccordionOnMobile = withStandardComponentWrapper(SideNavContainer, false);
export const StaticSideBarLayout = withStandardComponentWrapper(SideNavContainer, false);
export const StaticFullLayout = withStandardComponentWrapper(SideNavContainer, false);
export const ParentChildLayout = withStandardComponentWrapper(SideNavContainer, false);
